import React from 'react';
import { Wifi, Bot, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthStatus } from '../Auth';
import { cn } from '../../lib/utils';
import { BRAND_NAME } from '../../config/branding';

interface SystemStatusBarProps {
  className?: string;
}

/**
 * SystemStatusBar - Top status bar with system information
 *
 * Displays:
 * - Connection status (LED indicator)
 * - AI status
 * - Theme toggle
 * - Authentication status
 */
const SystemStatusBar: React.FC<SystemStatusBarProps> = React.memo(({ className }) => {
  const { theme, toggleTheme } = useTheme();

  return (
    <header
      className={cn(
        'sticky top-0 z-40',
        'bg-surface-container-low/95 backdrop-blur-sm',
        'border-b border-border-subtle',
        'px-3 py-1.5 md:px-4 md:py-2',
        'min-h-[44px] flex items-center',
        'pt-[max(0.375rem,env(safe-area-inset-top))]',
        // Let taps pass through non-control areas so content under the sticky bar stays clickable on very narrow viewports
        'pointer-events-none',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 md:gap-4 w-full">
        {/* Left: Status Indicators */}
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-accent-bullish/10">
            <span className="status-led status-led-online" />
            <Wifi className="h-3.5 w-3.5 text-accent-bullish" />
            <span className="text-xs font-medium text-accent-bullish hidden sm:inline">
              Live
            </span>
          </div>

          {/* AI Status */}
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-accent-primary/10">
            <Bot className="h-3.5 w-3.5 text-accent-primary" />
            <span className="text-xs font-medium text-accent-primary hidden sm:inline">
              AI Active
            </span>
          </div>
        </div>

        {/* Center: Brand (visible on mobile) */}
        <div className="flex-1 text-center md:hidden">
          <span className="text-sm font-semibold text-text-primary whitespace-nowrap">
            {BRAND_NAME}
          </span>
        </div>

        {/* Right: User Controls */}
        <div className="flex items-center gap-2 pointer-events-auto">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={cn(
              'p-2 rounded-lg',
              'text-text-secondary hover:text-text-primary',
              'hover:bg-surface-container',
              'transition-colors',
              'min-h-[44px] min-w-[44px]',
              'flex items-center justify-center'
            )}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'light' ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </button>

          {/* Auth Status */}
          <AuthStatus />
        </div>
      </div>
    </header>
  );
});

SystemStatusBar.displayName = 'SystemStatusBar';

export default SystemStatusBar;
